import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@kinesis/bungle';
import { Center } from '@/components/center';
import { Loading } from '@/components/loading';
import { useSessionQuery } from '@/api/login';
import {
  ShareAccepted,
  ShareAccount,
  isPendingInvitation,
  useAcceptShareMutation,
  useGetShareQuery,
} from '@/api/share';
import { setLastLogin } from '@/services/last-login';
import { sharedPathnameFor } from '@/services/launch';
import {
  isPasswordRejection,
  passwordRejectionMessage,
  passwordRejectionReason,
} from '@/services/password';
import { ShareClaimForm } from './share-claim-form';
import { ShareMessage } from './share-message';

// What the screen is showing, once the redeem-time answer is in. The share's
// state at redeem time is authoritative: the emailed copy was written when
// the share was issued and may have assumed the recipient had (or lacked) an
// account.
type Outcome =
  | { outcome: 'pending' }
  | { outcome: 'claimed' }
  | { outcome: 'not-found' }
  // The recipient has an organisation invitation waiting: it has to be
  // accepted before there is an account to claim the share with, and it
  // has just been sent again.
  | { outcome: 'pending-invitation' }
  | { outcome: 'failed' };

// A claim lands on the thing that was shared. Diplomat names it by
// namespace and key; zoo maps that to a path. A namespace zoo has no path
// for is not guessed at — the claim still succeeded, so the confirmation
// stands in for the destination.
const openShared = (accepted: ShareAccepted) => {
  const pathname = sharedPathnameFor(accepted?.object);
  if (!pathname) {
    return false;
  }
  window.location.assign(pathname);
  return true;
};

const ShareAccept = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { data: share, isLoading: isShareLoading } = useGetShareQuery(token);
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const [accept, { isLoading: isAccepting }] = useAcceptShareMutation();
  const [state, setState] = useState<Outcome>({ outcome: 'pending' });
  const [attempted, setAttempted] = useState(false);
  const [passwordRejection, setPasswordRejection] = useState<
    string | undefined
  >(undefined);
  const toast = useToast('globalTop');

  const loggedIn = !!session?.accessToken;

  // A session that did not resolve is not a session. This is a public page,
  // and the claim below goes up as whatever the screen is treating the
  // visitor as: a credential left in storage by an expired session would
  // still ride along on the claim and be refused at the gateway before
  // diplomat saw the token, losing the form they just filled in. Drop it
  // once the session query has answered and answered no.
  useEffect(() => {
    if (isSessionLoading || loggedIn) {
      return;
    }
    localStorage.removeItem('kinesis:session');
  }, [isSessionLoading, loggedIn]);

  // A claimant who is already logged in claims the token as themselves —
  // including a share whose state is spent, where diplomat answers success
  // if they were the acceptor and not-found if they were not.
  useEffect(() => {
    if (isShareLoading || isSessionLoading || attempted) {
      return;
    }
    if (!loggedIn || !share) {
      return;
    }
    if (share.state !== 'pending' && share.state !== 'spent') {
      return;
    }
    setAttempted(true);
    accept({ token })
      .unwrap()
      .then((accepted) => {
        if (!openShared(accepted)) {
          setState({ outcome: 'claimed' });
        }
      })
      .catch((e) =>
        setState({
          outcome: e?.status === 404 ? 'not-found' : 'failed',
        }),
      );
  }, [
    accept,
    attempted,
    isSessionLoading,
    isShareLoading,
    loggedIn,
    share,
    token,
  ]);

  const onLogin = useCallback(() => {
    if (share?.email) {
      setLastLogin(share.email);
    }
    localStorage.setItem('kinesis:redirect', window.location.pathname);
    window.location.assign('/');
  }, [share]);

  // SSO: there is no account to create here. Logging in provisions the
  // identity in friend, and the claim URL is stored so the user comes back to
  // it — arriving with a usable account, which the logged-in path claims with.
  const onSsoLogin = useCallback(() => {
    if (!share?.sso) {
      return;
    }
    if (share.email) {
      setLastLogin(share.email);
    }
    localStorage.setItem('kinesis:redirect', window.location.pathname);
    window.location.assign(share.sso.redirectUrl);
  }, [share]);

  const onClaim = useCallback(
    (account: ShareAccount) => {
      setPasswordRejection(undefined);
      accept({ token, account })
        .unwrap()
        .then((accepted) => {
          if (accepted.accessToken) {
            localStorage.setItem('kinesis:session', accepted.accessToken);
          }
          if (share?.email) {
            setLastLogin(share.email);
          }
          if (!openShared(accepted)) {
            setState({ outcome: 'claimed' });
          }
        })
        .catch((e) => {
          // A pending invitation is an answer, and the screen becomes it. A
          // failed claim is not: the details they typed are still in the form,
          // so the form stays and the toast asks for another go at it.
          if (isPendingInvitation(e)) {
            setState({ outcome: 'pending-invitation' });
            return;
          }
          // A refused password is a fixable mistake in one field, and the
          // reason is the only account of what was wrong: it goes against
          // the field, as it does on registration.
          if (isPasswordRejection(e)) {
            setPasswordRejection(
              passwordRejectionMessage(passwordRejectionReason(e)),
            );
            return;
          }
          toast('We couldn’t open this share. Please try again.', {
            variant: 'error',
            duration: 6000,
          });
        });
    },
    [accept, share, toast, token],
  );

  if (isShareLoading || isSessionLoading) {
    return <Loading />;
  }

  const label = share?.label ?? 'this';
  const sharer = share?.sharerName;

  return (
    <Center>
      {state.outcome === 'claimed' && (
        <ShareMessage title='Shared with you'>
          You now have access to <strong>{label}</strong>
          {sharer ? `, shared by ${sharer}` : ''}.
        </ShareMessage>
      )}
      {state.outcome === 'not-found' && (
        <ShareMessage title='Share not found'>
          This share isn’t available. Ask whoever shared it with you to share it
          again.
        </ShareMessage>
      )}
      {state.outcome === 'pending-invitation' && (
        <ShareMessage title='Accept your invitation first'>
          You have an invitation to the Kinesis platform waiting. We’ve sent it
          to <strong>{share?.email}</strong> again — accept it, then open this
          link to claim what’s been shared with you.
        </ShareMessage>
      )}
      {state.outcome === 'failed' && (
        <ShareMessage title='Something went wrong'>
          We couldn’t open this share. Please try again.
        </ShareMessage>
      )}
      {state.outcome === 'pending' && (
        <>
          {/* Logged in: the accept above is in flight or has answered. */}
          {loggedIn &&
            (share?.state === 'pending' || share?.state === 'spent') && (
              <Loading />
            )}
          {share?.state === 'not-found' && (
            <ShareMessage title='Share not found'>
              This share isn’t available. Ask whoever shared it with you to
              share it again.
            </ShareMessage>
          )}
          {share?.state === 'expired' && (
            <ShareMessage title='Share expired'>
              This share has expired. Ask whoever shared it with you to share it
              again.
            </ShareMessage>
          )}
          {!loggedIn && share?.state === 'spent' && (
            <ShareMessage
              title='Already claimed'
              action={{ label: 'Log in', onClick: onLogin }}
            >
              This share has been claimed. Log in to open it.
            </ShareMessage>
          )}
          {!loggedIn && share?.state === 'pending' && share.usableAccount && (
            <ShareMessage
              title='Shared with you'
              action={{ label: 'Log in', onClick: onLogin }}
            >
              {sharer ? `${sharer} shared ` : 'Someone shared '}
              <strong>{label}</strong> with you. Log in to open it.
            </ShareMessage>
          )}
          {/* No usable account, address authenticates by SSO: no password is
              ever asked for, they are sent to log in. */}
          {!loggedIn &&
            share?.state === 'pending' &&
            !share.usableAccount &&
            !!share.sso && (
              <ShareMessage
                title='Shared with you'
                action={{
                  label: `Log in via ${share.sso.organisation}`,
                  onClick: onSsoLogin,
                }}
              >
                {sharer ? `${sharer} shared ` : 'Someone shared '}
                <strong>{label}</strong> with you. Log in with your{' '}
                <strong>{share.sso.organisation}</strong> account to open it.
              </ShareMessage>
            )}
          {!loggedIn &&
            share?.state === 'pending' &&
            !share.usableAccount &&
            !share.sso && (
              <ShareClaimForm
                email={share.email ?? ''}
                isClaiming={isAccepting}
                label={label}
                onClaim={onClaim}
                passwordRejection={passwordRejection}
                sharerName={sharer}
              />
            )}
        </>
      )}
    </Center>
  );
};

export { ShareAccept };
